import PropTypes from 'prop-types';
import React, { useRef, useState, useEffect, useCallback } from 'react';
import Player from 'react-player/lib/players/FilePlayer';
import Hls from 'hls.js';
import { Map } from 'immutable';
import moment from 'moment';
import classNames from 'classnames';

import { track } from '@xanadu/ui-shared/interfaces/woopra';
import useInterval from '@xanadu/ui-shared/hooks/useInterval';

import Spinner from '@xanadu/ui-shared/components/widgets/Spinner';
import Locked from './Locked';
import { createRMGPlayerScript, toggleCinemaMode } from './utils';
import IconClose from '../../../images/icons/misc/close.svg';
import CinemaModeIcon from '../../../images/icons/white/cinemaMode.svg';

import styles from './Streaming.module.scss';

const STREAM_END_TIME_THRESHOLD = 6; // minutes
const HR_USA_STREAM_END_TIME_THRESHOLD = 90; // minutes

const RMG_PLAY_BUTTON_THRESHOLD = 2;

export default function Streaming({
  loginRequest,
  isLoggedIn,
  sportUrlName,
  getStream,
  onEnablePIP,
  onDisablePIP,
  pip,
  pipEvent,
  streamDetails,
  pipEnabled,
  currentEvent,
  streamAvailable,
  nextStream,
  matchedAmount,
  mobileStream,
  isClosed,
  isHorseRace,
  isUSARace = false,
  isEventAvailableForStreaming,
  minimumBetAmount,
}) {
  const player = useRef();
  const callbackLastUpdate = useRef(0);
  const timeout = useRef();
  const [trigger, setTrigger] = useState(false);
  const [playing, setPlayPause] = useState(false);
  const [hasPlayed, setStarted] = useState(false);
  const [currentStream, setCurrentStream] = useState(streamDetails);
  const [currentStreamError, setCurrentStreamError] = useState();
  const [activeStream, setActiveStream] = useState(false);
  const [finishTime, setFinishTime] = useState(false);
  const [eventFinished, setEventFinished] = useState(false);
  const [autoPlay, setAutoPlay] = useState(false);
  const [timeToStart, setTimeToStart] = useState();
  const [rmgLoading, setRmgLoading] = useState(false);
  const [cinemaMode, setCinemaMode] = useState(false);

  const { rmg } = currentStream;

  const script = document.createElement('script');

  const endTimeStreamThreshold = isUSARace
    ? HR_USA_STREAM_END_TIME_THRESHOLD
    : STREAM_END_TIME_THRESHOLD;

  useInterval(() => {
    setTrigger(!trigger);
  }, 15000);

  const playFunction = useCallback(() => {
    if ((rmg && timeToStart < RMG_PLAY_BUTTON_THRESHOLD) || !rmg) {
      setAutoPlay(true);
      track('streaming', 'Play Stream');
    }
  }, [rmg, timeToStart]);

  const scriptInit = useCallback(node => {
    if (rmg && node && autoPlay) {
      if (Date.now() - callbackLastUpdate.current > 1000) {
        callbackLastUpdate.current = Date.now();
        setRmgLoading(true);
        // check for existing dead scripts/frames
        const leftoverScripts = node.getElementsByTagName('script') || [];
        for (let i = 0; i < leftoverScripts.length; i++) {
          node.removeChild(leftoverScripts[i]);
        }
        const leftoverFrames = node.getElementsByTagName('iframe') || [];
        for (let j = 0; j < leftoverFrames.length; j++) {
          node.removeChild(leftoverFrames[j]);
        }
        const checkForIframe = () => {
          // the csb v2 player renders a <csb-widget> instead of an iframe and may remove its script
          const playerRendered = Array.from(node.children).some(child => child.tagName !== 'SCRIPT');
          if (playerRendered) {
            setRmgLoading(false);
            clearTimeout(timeout.current);
            return;
          }
          // TODO: improve me! this doesn't get removed if the iframe never shows up
          timeout.current = setTimeout(checkForIframe, 0);
        };
        createRMGPlayerScript(script, currentStream);

        node.appendChild(script);
        checkForIframe();
      }
    }
  }, [autoPlay, rmg, currentStream, callbackLastUpdate]);

  const retry = useCallback(() => {
    getStream({ event: currentEvent, isNextStreamRequest: false });
  });

  useEffect(() => {
    window.Hls = Hls;
    return () => {
      window.Hls = undefined;
      if (timeout.current) clearTimeout(timeout.current);
    };
  }, []);

  useEffect(() => {
    if (!pipEvent && currentEvent) {
      setStarted(false);
      setAutoPlay(false);
      setPlayPause(false);
      setActiveStream(false);
      setEventFinished(false);
      setFinishTime();
      setTimeToStart();
      if (timeout.current) clearTimeout(timeout.current);
    }
  }, [currentEvent]);

  useEffect(() => {
    if (
      isLoggedIn &&
      isEventAvailableForStreaming &&
      currentEvent &&
      ((mobileStream && !playing) || !mobileStream)
    ) {
      if (pipEnabled && currentStream && currentEvent !== pipEvent) {
        getStream({ event: currentEvent, isNextStreamRequest: true });
      } else if (!isClosed) {
        getStream({ event: currentEvent, isNextStreamRequest: false });
      }
    }
  }, [
    matchedAmount,
    currentEvent,
    isLoggedIn,
    isEventAvailableForStreaming,
    mobileStream,
    playing,
    pipEnabled,
  ]);

  useEffect(() => {
    if (pipEnabled && nextStream) {
      setCurrentStream(nextStream);
      setCurrentStreamError(nextStream.error);
    } else if (pipEnabled && !nextStream && streamDetails.get(pipEvent)) {
      setCurrentStream(streamDetails.get(pipEvent));
      setCurrentStreamError(streamDetails.get(pipEvent).error);
    } else if (streamDetails.get(currentEvent)) {
      setCurrentStream(streamDetails.get(currentEvent));
      setCurrentStreamError(streamDetails.get(currentEvent).error);
    }
  }, [streamDetails, nextStream, currentEvent, pipEvent, pipEnabled]);

  useEffect(() => {
    if (currentStream && currentStream.endTime && currentStream.startTime) {
      const currentDate = moment();
      const endDate = moment(currentStream.endTime).utc();
      const startDate = moment(currentStream.startTime).utc();
      const diff = moment.duration(startDate.subtract(1, 'm').diff(currentDate)).asMinutes();
      setTimeToStart(Math.round(diff));
      let isBetween;

      if (pip) {
        isBetween =
          currentDate.isSameOrAfter(startDate.subtract(1, 'm')) &&
          currentDate.isSameOrBefore(endDate.add(endTimeStreamThreshold, 'm'));
      } else if (isClosed && finishTime) {
        isBetween =
          currentDate.isSameOrAfter(startDate.subtract(1, 'm')) &&
          currentDate.isSameOrBefore(finishTime);
      } else if (!isClosed) {
        isBetween = currentDate.isSameOrAfter(startDate.subtract(1, 'm'));
      }
      if (isBetween) {
        setActiveStream(isBetween);
      } else {
        setActiveStream(false);
      }
    } else if (streamDetails) {
      setCurrentStream(streamDetails);
    }
  }, [currentStream, currentEvent, trigger, finishTime, endTimeStreamThreshold]);

  useEffect(() => {
    if (isClosed && currentStream && currentStream.endTime) {
      setFinishTime(
        moment(currentStream.endTime)
          .utc()
          .add(endTimeStreamThreshold, 'minutes')
      );
    } else {
      setFinishTime();
    }
  }, [isClosed, endTimeStreamThreshold]);

  useEffect(() => {
    if (finishTime) {
      const currentDate = moment();
      const isFinished = currentDate.isAfter(finishTime);

      setEventFinished(isFinished);
    }
  }, [finishTime, trigger]);

  useEffect(() => {
    setPlayPause(autoPlay);
  }, [autoPlay]);

  useEffect(() => {
    setStarted(playing);
  }, [playing]);

  useEffect(() => {
    if (eventFinished) {
      setStarted(false);
      setAutoPlay(false);
      setPlayPause(false);
      setActiveStream(false);
      setCinemaMode(false);
    }
  }, [eventFinished]);

  useEffect(() => {
    if (!mobileStream) {
      setAutoPlay(false);
      setPlayPause(false);
      setCinemaMode(false);
    }
  }, [mobileStream]);

  useEffect(() => {
    if (!isLoggedIn) {
      setCinemaMode(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    const currentDate = moment();
    const endDate = moment(currentStream.endTime).utc();
    if (
      pip &&
      pipEvent !== currentEvent &&
      currentDate.isSameOrAfter(endDate.add(endTimeStreamThreshold, 'm'))
    ) {
      setStarted(false);
      setAutoPlay(false);
      setPlayPause(false);
      onDisablePIP();
    }
  }, [pip, trigger, endTimeStreamThreshold]);

  useEffect(() => {
    toggleCinemaMode(
      cinemaMode,
      document.getElementsByClassName(`${styles.scalable}`)[0],
      styles.scaled
    );
  }, [cinemaMode]);

  if (sportUrlName === 'soccer') {
    return null;
  }

  const playIcon = playing
    ? '/assets/images/icons/misc/pause.svg'
    : '/assets/images/icons/misc/play.svg';
  const playStyle = {
    backgroundImage: `url(${playIcon})`,
  };

  const unlockedPlaceholderClass = classNames({
    [styles.unlockedPlaceholder]: true,
    [styles[`unlockedPlaceholder--${sportUrlName}`]]: true,
  });

  let hiddenStyle = styles['streaming--hide'];

  if (isEventAvailableForStreaming && streamAvailable) {
    hiddenStyle = '';
  }

  let pushToBack = '';
  if (pipEnabled && isEventAvailableForStreaming && pipEvent === currentEvent) {
    pushToBack = styles['streaming--back'];
  }

  let mobileStyle = '';
  let mobilePlaceholderStyle = '';
  let mobileVideoStyle = '';
  let rmgStyle = '';

  if (mobileStream) {
    mobileStyle = styles['streaming--mobile'];
    mobilePlaceholderStyle = styles.placeholder;
    mobileVideoStyle = styles['video--mobile'];

    if (currentStream && rmg) {
      rmgStyle = styles['streaming--mobile-rmg'];
    }
  }

  if (!isLoggedIn || currentStreamError || eventFinished) {
    return (
      <Locked
        extraClass={pushToBack}
        isHorseRace={isHorseRace}
        isLoggedIn={isLoggedIn}
        loginRequest={loginRequest}
        streamError={currentStreamError || ''}
        hide={hiddenStyle !== ''}
        eventFinished={eventFinished}
        mobileStream={mobileStream}
        sportUrlName={sportUrlName}
        minimumBetAmount={minimumBetAmount}
      />
    );
  }

  if (!hasPlayed) {
    const isNotRmgReady = rmg && !(timeToStart < RMG_PLAY_BUTTON_THRESHOLD);

    return (
      <div className={`${styles.streaming} ${hiddenStyle} ${pushToBack} ${mobileStyle}`}>
        {!mobileStream && <div className={styles.header}>Matchbook Live Stream</div>}
        <div className={`${unlockedPlaceholderClass} ${mobilePlaceholderStyle}`} />
        <div className={`${styles.controls} ${styles.unlockedControls}`}>
          {isNotRmgReady
            ? <span className={styles.rmgDisclaimer}>
              Streaming will be available shortly before the race
            </span>
            : (
              <div
                role="button"
                style={playStyle}
                className={styles.playPause}
                onClick={playFunction}
              />
            )
          }
        </div>
      </div>
    );
  }

  if (!activeStream && autoPlay && !eventFinished) {
    let streamMsg = 'Waiting for race to start';
    if (timeToStart > 0) {
      streamMsg = `Stream will start in ${Math.abs(timeToStart)} minute${timeToStart > 1 ? 's' : ''}`;
    }
    return (
      <div className={`${styles.streaming} ${hiddenStyle} ${pushToBack} ${mobileStyle}`}>
        {!mobileStream && <div className={styles.header}>Matchbook Live Stream</div>}
        <div className={`${unlockedPlaceholderClass} ${mobilePlaceholderStyle}`} />
        <div className={styles.waitText}>{streamMsg}</div>
      </div>
    );
  }
  if (currentStreamError && currentStreamError.errorMessage !== null) {
    return (
      <div className={`${styles.streaming} ${hiddenStyle} ${pushToBack} ${mobileStyle}`}>
        {!mobileStream && <div className={styles.header}>Matchbook Live Stream</div>}
        <div className={`${styles.error} ${mobilePlaceholderStyle}`}>
          <div className={styles.errorText}>Stream is currently unavailable</div>
          <div role="button" className={styles.retry} onClick={() => retry()} />
          <div className={styles.errorText}>Click to try again</div>
        </div>
      </div>
    );
  }
  if (activeStream && autoPlay && currentStream && currentStream.url) {
    return (
      <div className={`${styles.streaming} ${hiddenStyle} ${pushToBack} ${mobileStyle}`}>
        {!mobileStream && <div className={styles.header}>Matchbook Live Stream</div>}
        <Player
          ref={player}
          className={`${styles.video} ${mobileVideoStyle}`}
          height="198px"
          width="352px"
          url={currentStream.url}
          playing={playing}
          muted
          controls
          pip={pip}
          onEnablePIP={onEnablePIP}
          onDisablePIP={onDisablePIP}
          onError={e => {
            // eslint-disable-next-line no-console
            console.warn(e);
          }}
        />
      </div>
    );
  }
  if (currentStream && rmg) {
    return (
      <div
        // the following key is useful to get rid of the existing player and script when switching tab
        key={`externalPlayer-${currentEvent}-${rmg.fixtureUuid}-${rmg.livestreamUuid}`}
        className={`${styles.streaming} ${styles['streaming--rmg']} ${hiddenStyle} ${pushToBack} ${mobileStyle} ${rmgStyle}`}
      >
        {!mobileStream && <div className={styles.header}>Matchbook Live Stream</div>}
        <div className={styles.scalable} ref={scriptInit} />
        {rmgLoading && (
          <div className={styles['spinnerContainer--rmg']}>
            <Spinner
              size="large"
              theme="light"
            />
          </div>
        )}
        {cinemaMode && (
          <svg viewBox="0 0 10 10" className={styles.cinemaSvg}>
            <path d="M0 0 L0 150 L150 150 L150 0 Z" className={styles.cinemaOverlay} />
          </svg>
        )}
        {!rmgLoading && (
          <div id="rmgControls" className={styles.rmgControlsContainer}>
            <div
              className={styles.controlButton}
              role="button"
              onClick={() => {
                setCinemaMode(!cinemaMode);
              }}
            >
              <CinemaModeIcon className={styles.largeIcon} />
            </div>
            <div
              className={styles.controlButton}
              role="button"
              onClick={() => {
                setAutoPlay(false);
                setCinemaMode(false);
              }}
            >
              <IconClose />
            </div>
          </div>
        )}
      </div>
    );
  }
  return (
    <div className={`${styles.streaming} ${hiddenStyle} ${pushToBack} ${mobileStyle}`}>
      {!mobileStream && <div className={styles.header}>Matchbook Live Stream</div>}
      <div className={`${styles.loading} ${mobilePlaceholderStyle}`}>
        <Spinner size="large" theme="light" />
      </div>
    </div>
  );
}

Streaming.propTypes = {
  isLoggedIn: PropTypes.bool.isRequired,
  isHorseRace: PropTypes.bool,
  isUSARace: PropTypes.bool,
  pip: PropTypes.bool.isRequired,
  onEnablePIP: PropTypes.func,
  onDisablePIP: PropTypes.func,
  loginRequest: PropTypes.func.isRequired,
  getStream: PropTypes.func.isRequired,
  streamDetails: PropTypes.instanceOf(Map),
  pipEnabled: PropTypes.bool,
  currentEvent: PropTypes.string,
  pipEvent: PropTypes.string,
  streamAvailable: PropTypes.string,
  nextStream: PropTypes.object,
  matchedAmount: PropTypes.number,
  mobileStream: PropTypes.bool,
  isClosed: PropTypes.bool,
  isEventAvailableForStreaming: PropTypes.bool,
  sportUrlName: PropTypes.string,
  minimumBetAmount: PropTypes.number,
};
